import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core'
import { Currency, Depot, Product, ProductMove, ProductMovePack, Supplier } from 'depoto-core/src/entities'
import { ModalContainerComponent } from '../../_common/modal-container/modal-container.component'
import { CoreService, NotificationService } from '../../../services'
import { SchemaUtil } from '../../../utils'
import { TranslateService } from '@ngx-translate/core'
import * as Sentry from '@sentry/angular-ivy'
import { CompanyUtilService } from '../../../services/company-util.service'

@Component({
  selector: 'modal-stock-load',
  templateUrl: 'modal-stock-load.component.html',
  styleUrls: ['modal-stock-load.component.scss'],
})
export class ModalStockLoadComponent implements OnInit {
  @ViewChild('modal') public childModal: ModalContainerComponent
  @Input() public product: Product
  @Input() public suppliers: Supplier[] = []
  @Input() public depots: Depot[] = []
  @Input() public currencies: Currency[] = []
  @Input() public isUnloadingStock = false
  @Input() public currentStockItem: any
  @Input() public btnXs = false
  @Output() public result: EventEmitter<void> = new EventEmitter()
  public productMove: ProductMove = new ProductMove()
  loading = true
  keyEventSubscriptions: any = {}
  headerTitle: string
  headerBgClass: string
  bodyBgClass: string
  footerBgClass: string
  submitted = false

  constructor(
    private core: CoreService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    public companyUtil: CompanyUtilService,
  ) {}

  ngOnInit() {
    if (this.isUnloadingStock) {
      this.headerTitle = 'out'
      this.headerBgClass = 'header-class bg_red'
      this.bodyBgClass = 'bg_red'
      this.footerBgClass = 'bg_red'
    } else if (!this.isUnloadingStock) {
      this.headerTitle = 'in'
      this.headerBgClass = 'header-class bg_green'
      this.bodyBgClass = 'bg_green'
      this.footerBgClass = 'bg_green'
    }
  }

  loadDepotsAndSuppliers(): void {
    this.loading = true
    this.productMove.batch = ''
    this.productMove.expirationDate = ''
    if (
      (!this.productMove.supplier || this.productMove.supplier < 1) &&
      this.suppliers &&
      this.suppliers.length === 1
    ) {
      this.productMove.supplier = this.suppliers[0].id
    } else if (this.product.supplier?.id > 0) {
      this.productMove.supplier = this.product.supplier.id
    }
    const lastSupplier =
      this.core.services.storage.getSync(this.core.services.storage.keys.views.lastStockItemSupplier) || null
    const doesLastSupplierExists = this.suppliers.map(s => s.id).filter(s => s === lastSupplier).length > 0
    if (!this.productMove.supplier && lastSupplier && doesLastSupplierExists) {
      this.productMove.supplier = lastSupplier
    }
    const lastDepot =
      this.core.services.storage.getSync(this.core.services.storage.keys.views.lastStockItemDepot) || null
    const doesLastDepotExists = this.depots.map(d => d.id).filter(d => d === lastDepot).length > 0
    if (lastDepot && doesLastDepotExists) {
      this.productMove.depotTo = lastDepot
      this.productMove.purchasePrice = 0
    }
    if ((!this.productMove.depotTo || this.productMove.depotTo < 1) && this.depots && this.depots.length === 1) {
      this.productMove.depotTo = this.depots[0].id
    }
    this.fillLastUsedValues()
  }

  private fillDepotValue() {
    const doesLastDepotExists = this.depots.map(d => d.id).filter(d => d === this.currentStockItem.depot.id).length > 0
    if (doesLastDepotExists) {
      this.productMove.depotTo = this.currentStockItem.depot.id
    } else if ((!this.productMove.depotTo || this.productMove.depotTo < 1) && this.depots && this.depots.length === 1) {
      this.productMove.depotTo = this.depots[0].id
    }
  }

  private fillSupplierValue() {
    const doesLastSupplierExists =
      this.suppliers.map(s => s.id).filter(s => s === this.currentStockItem.supplier.id).length > 0
    if (doesLastSupplierExists) {
      this.productMove.supplier = this.currentStockItem.supplier.id
    } else if (
      (!this.productMove.supplier || this.productMove.supplier < 1) &&
      this.suppliers &&
      this.suppliers.length === 1
    ) {
      this.productMove.supplier = this.suppliers[0].id
    }
  }

  fillLastUsedValues() {
    if (this.currentStockItem) {
      if (this.currentStockItem.depot && this.currentStockItem.depot.id > 0) {
        this.fillDepotValue()
      }
      if (this.currentStockItem.supplier && this.currentStockItem.supplier.id > 0) {
        this.fillSupplierValue()
      }
      this.productMove.purchasePrice = this.currentStockItem.purchasePrice
      if (this.product.purchaseCurrency) {
        this.productMove.purchaseCurrency = this.product.purchaseCurrency.id
      }
      this.productMove.amount = 1
      this.productMove.batch = this.currentStockItem.batch
      if (this.currentStockItem.expirationDate && this.currentStockItem.expirationDate.length > 0) {
        this.productMove.expirationDate = this.currentStockItem.expirationDate.substring(0, 10)
      }
      if (this.currentStockItem.position1 && this.currentStockItem.position1.length > 0) {
        this.productMove.position1 = this.currentStockItem.position1
      }
      if (this.currentStockItem.position2 && this.currentStockItem.position2.length > 0) {
        this.productMove.position2 = this.currentStockItem.position2
      }
      if (this.currentStockItem.position3 && this.currentStockItem.position3.length > 0) {
        this.productMove.position3 = this.currentStockItem.position3
      }
    } else {
      this.productMove.amount = 1
      this.productMove.purchasePrice = 0
    }
    if (this.product && this.product.supplier && this.product.supplier.id > 0 && !this.productMove.supplier) {
      this.productMove.supplier = this.product.supplier.id
    }
  }

  async getProductPack() {
    try {
      const result = await this.core.services.productPack.getList(
        { filters: { product: this.product.id } },
        SchemaUtil.productPack,
      )
      this.productMove.amount = result.items[0]?.quantity ? result.items[0]?.quantity : 1
      let a = Number(this.product.purchasePrice) / Number(this.productMove.amount)
      a = Number(a.toFixed(2))
      this.productMove.purchasePrice = a
    } catch (err) {
      console.log(err)
    } finally {
      this.loading = false
    }
  }

  async execute() {
    this.submitted = true
    if (!this.isUnloadingStock && (!this.productMove.depotTo || this.productMove.depotTo < 1)) {
      return
    }
    if (isNaN(Number(this.productMove.amount))) {
      alert(this.translateService.instant('modal-stock-load.wrong-quantity'))
      return
    }
    if (!this.isUnloadingStock && isNaN(Number(this.productMove.purchasePrice))) {
      alert(this.translateService.instant('modal-stock-load.wrong-price'))
      return
    }
    if (!this.isUnloadingStock && Number(this.productMove.amount) <= 0) {
      alert(this.translateService.instant('modal-stock-load.no-store-in-minus'))
      return
    }
    if (
      this.isUnloadingStock &&
      this.currentStockItem &&
      this.currentStockItem.quantityStock < Number(this.productMove.amount)
    ) {
      alert(this.translateService.instant('modal-stock-load.out', { quantity: this.currentStockItem.quantityStock }))
      this.productMove.amount = this.currentStockItem.quantityStock
      return
    }
    if (!this.isUnloadingStock && !this.productMove.supplier) {
      return
    }
    this.loading = true
    const movePack: ProductMovePack = new ProductMovePack({
      type: { id: this.isUnloadingStock ? 'out' : 'in' },
      note: this.productMove.note,
      moves: [this.createProductMove()],
    })
    const result = await this.core.services.productMovePack.create(movePack)
    this.loading = false
    if (result) {
      this.result.emit()
      const result = this.isUnloadingStock ? 'alert.unloading.stock' : 'alert.loading.stock'
      this.notificationService.success(this.translateService.instant(result))
      this.hideChildModal()
    } else {
      this.notificationService.error(this.translateService.instant('modal-stock-load.error'))
    }
  }

  createProductMove(): ProductMove {
    if (isNaN(Number(this.productMove.amount))) {
      alert(this.translateService.instant('modal-stock-load.wrong-quantity'))
      return
    }
    const productMove = new ProductMove({
      ...this.productMove,
      purchasePriceSum: Number(this.productMove.purchasePrice) * Number(this.productMove.amount),
      product: this.product,
      purchaseCurrency: this.product?.purchaseCurrency?.id,
    })
    if (this.currentStockItem) {
      ;['purchaseCurrencyDate'].forEach(
        // api not ok without product/depots ids..
        prop => delete productMove[prop],
      )
      if (this.isUnloadingStock) {
        productMove.depotFrom = this.currentStockItem.depot.id
      }
      productMove.productDepot = this.currentStockItem.id
    }
    return productMove
  }

  async showChildModal() {
    this.loading = true
    this.keyEventSubscriptions.scannedPositions = this.core.services.keyEvent.onScannedPositions
      .subscribe()
      .then(positions => {
        this.productMove.position1 = positions[0]
        this.productMove.position2 = positions[1]
        this.productMove.position3 = positions[2]
      })
    this.loadDepotsAndSuppliers()
    // set purchasePrice if its count-by-piece product and its not on concrete expiration
    if (
      !this.currentStockItem &&
      this.product &&
      this.product.tags.some(tag => tag.externalId === 'hs-count-by-piece')
    ) {
      await this.getProductPack()
    } else {
      if (!this.currentStockItem) {
        this.productMove.amount = 1
        this.productMove.purchasePrice = this.product.purchasePrice
      }
      this.loading = false
    }
    this.childModal.showChildModal()
  }

  async setPurchasePrice() {
    try {
      const result = await this.core.services.productPack.getList(
        { filters: { product: this.product.id } },
        SchemaUtil.productPack,
      )
      if (result.items?.length) {
        this.productMove.purchasePrice = Number((this.product.purchasePrice / result.items[0].piecesInPack).toFixed(2))
      }
    } catch (err) {
      console.log(err)
    }
  }

  hideChildModal(): void {
    if (this.keyEventSubscriptions.scannedPositions) {
      this.keyEventSubscriptions.scannedPositions.unsubscribe()
      this.keyEventSubscriptions.scannedPositions = null
    }
    this.childModal.hideChildModal()
  }

  number(number: number) {
    return number
  }
}
